import { assertNever, isDefined } from 'common';
import {
  LLM_MODELS,
  type LlmModel,
  LlmModelIntelligenceTier,
  LlmModelLifecycleState,
  LlmModelName,
  LlmProvider,
  getLlmModelIntelligenceTier,
  getLlmModelLifecycleState,
  isLlmModelOfferable,
} from 'llm-model-registry';

/** A single Autocomplete option: a concrete model, or the "Auto" recommendation. */
export interface LlmModelPickerOption {
  id: string;
  model: LlmModel;
  group?: string;
}

export const LLM_MODEL_PICKER_RECOMMENDED_GROUP = 'Recommended';
export const LLM_MODEL_PICKER_SINGULAR_AUTO_ID = 'auto';
/** Display label for the singular "Auto" recommendation. */
export const LLM_MODEL_PICKER_AUTO_OPTION_LABEL = 'Auto (Recommended)';

/** "Auto" is the only meta-option; every other option is a concrete model. */
export function isLlmModelPickerAutoOptionId(id: string): boolean {
  return id === LLM_MODEL_PICKER_SINGULAR_AUTO_ID;
}

/**
 * The "Auto" recommendation option — always offered and shown first in its own
 * group. Exposed separately so callers have a guaranteed non-null fallback for a
 * required selection (the model field can never be empty).
 */
export function buildLlmModelPickerAutoOption(): LlmModelPickerOption {
  return {
    id: LLM_MODEL_PICKER_SINGULAR_AUTO_ID,
    model: {
      ...LLM_MODELS[LlmModelName.notable_default],
      name: LLM_MODEL_PICKER_AUTO_OPTION_LABEL,
    },
    group: LLM_MODEL_PICKER_RECOMMENDED_GROUP,
  };
}

const TIER_ORDER: LlmModelIntelligenceTier[] = [
  LlmModelIntelligenceTier.Frontier,
  LlmModelIntelligenceTier.Strong,
  LlmModelIntelligenceTier.Moderate,
  LlmModelIntelligenceTier.Basic,
];

export const LLM_MODEL_PICKER_TIER_LABELS: Record<LlmModelIntelligenceTier, string> = {
  [LlmModelIntelligenceTier.Frontier]: 'Frontier Intelligence',
  [LlmModelIntelligenceTier.Strong]: 'Strong Intelligence',
  [LlmModelIntelligenceTier.Moderate]: 'Moderate Intelligence',
  [LlmModelIntelligenceTier.Basic]: 'Basic Intelligence',
};

export const LLM_MODEL_PICKER_TIER_CAPABILITY_DESCRIPTIONS: Record<
  LlmModelIntelligenceTier,
  string
> = {
  [LlmModelIntelligenceTier.Frontier]:
    'Complex reasoning, nuanced judgment, and multi-step analysis',
  [LlmModelIntelligenceTier.Strong]: 'Detailed reasoning and contextual understanding',
  [LlmModelIntelligenceTier.Moderate]:
    'Structured tasks like extraction, summarization, and classification',
  [LlmModelIntelligenceTier.Basic]: 'Pattern matching, formatting, and simple extraction',
};

export const LLM_MODEL_PICKER_RECOMMENDED_DESCRIPTION_SINGULAR =
  "Automatically run a model that Notable has evaluated to be effective across a wide range of tasks. Model subject to change. Billed at the model's per-token rate.";

/**
 * Notice shown beneath the picker for the selected model, or `undefined` when
 * it is `Active` (the default helper text shows instead).
 */
export function getLlmModelPickerSelectedModelNotice(model: LlmModel): string | undefined {
  const state = getLlmModelLifecycleState(model);
  switch (state) {
    case LlmModelLifecycleState.Active:
      return undefined;
    case LlmModelLifecycleState.Retired:
      return 'This model is retired — it still runs, but switch to a current model when you can.';
    case LlmModelLifecycleState.Obsolete:
      return 'This model is obsolete and no longer available — switch to a current model.';
    case LlmModelLifecycleState.Upcoming:
      return `This model is not available until ${model.addedDate} — switch to a current model.`;
    default:
      return assertNever('Unhandled LLM model lifecycle state', state);
  }
}

const PROVIDER_LABELS: Record<LlmProvider, string> = {
  [LlmProvider.OpenAI]: 'OpenAI',
  [LlmProvider.Anthropic]: 'Anthropic',
  [LlmProvider.Google]: 'Google',
  [LlmProvider.Meta]: 'Meta',
  [LlmProvider.Amazon]: 'Amazon',
  [LlmProvider.DeepSeek]: 'DeepSeek',
};

/**
 * Vendor precedence for the in-tier sort: OpenAI → Google (Gemini) → Anthropic,
 * then everyone else. Chosen to subtly nudge toward higher-quota providers for
 * rate-limit relief; providers not listed share the trailing "other" rank.
 */
const VENDOR_SORT_ORDER: LlmProvider[] = [
  LlmProvider.OpenAI,
  LlmProvider.Google,
  LlmProvider.Anthropic,
];

function getVendorRank(model: LlmModel): number {
  if (!isDefined(model.provider)) {
    return VENDOR_SORT_ORDER.length;
  }
  const rank = VENDOR_SORT_ORDER.indexOf(model.provider);
  return rank === -1 ? VENDOR_SORT_ORDER.length : rank;
}

/** UTC epoch of the model's `addedDate` (release-date proxy); undated models sort last. */
function getAddedDateValue(model: LlmModel): number {
  return isDefined(model.addedDate) ? new Date(`${model.addedDate}T00:00:00Z`).getTime() : 0;
}

/**
 * In-tier ordering: vendor precedence (OpenAI → Gemini → Anthropic → other), then
 * Artificial Analysis Intelligence Index descending, then release date (addedDate)
 * descending, with the display name as a stable final tiebreak. The intelligence
 * tier itself is the group boundary, so it is applied by
 * {@link buildLlmModelPickerOptions} via TIER_ORDER.
 */
function compareByPickerSortOrder(modelA: LlmModel, modelB: LlmModel): number {
  const vendorDelta = getVendorRank(modelA) - getVendorRank(modelB);
  if (vendorDelta !== 0) {
    return vendorDelta;
  }
  const scoreDelta = (modelB.intelligenceScore ?? 0) - (modelA.intelligenceScore ?? 0);
  if (scoreDelta !== 0) {
    return scoreDelta;
  }
  const dateDelta = getAddedDateValue(modelB) - getAddedDateValue(modelA);
  if (dateDelta !== 0) {
    return dateDelta;
  }
  return modelA.name.localeCompare(modelB.name);
}

/** Lowercased haystack (name + provider + tier) used to filter the Autocomplete. */
export function getLlmModelPickerOptionSearchText(option: LlmModelPickerOption): string {
  const parts = [option.model.name];
  if (isDefined(option.model.provider)) {
    parts.push(PROVIDER_LABELS[option.model.provider]);
  }
  const tier = getLlmModelIntelligenceTier(option.model);
  if (isDefined(tier)) {
    parts.push(LLM_MODEL_PICKER_TIER_LABELS[tier]);
  }
  return parts.join(' ').toLowerCase();
}

/** Group-header copy for a rendered tier group. */
export function getLlmModelPickerGroupHeader(group: string): {
  title: string;
  description: string | undefined;
} {
  const tier = TIER_ORDER.find((candidate) => LLM_MODEL_PICKER_TIER_LABELS[candidate] === group);
  return {
    title: `${group} Models`,
    description: isDefined(tier) ? LLM_MODEL_PICKER_TIER_CAPABILITY_DESCRIPTIONS[tier] : undefined,
  };
}

/**
 * Builds the Autocomplete option list: a singular "Auto" recommendation on top,
 * followed by per-intelligence-tier groups of concrete models (each ordered by
 * {@link compareByPickerSortOrder}). The currently-selected model is always included so
 * saved configs stay visible even after a model is retired.
 */
export function buildLlmModelPickerOptions(config: {
  selectedModelId: string | undefined;
}): LlmModelPickerOption[] {
  const { selectedModelId } = config;
  const isVisible = (model: LlmModel): boolean =>
    isLlmModelOfferable(model) || model.id === selectedModelId;
  const concreteModels = Object.values(LLM_MODELS).filter(
    (model) => model.id !== LlmModelName.notable_default && isVisible(model)
  );

  const options: LlmModelPickerOption[] = [buildLlmModelPickerAutoOption()];

  for (const tier of TIER_ORDER) {
    const tierModels = concreteModels
      // Scoreless models fall into the Basic tier.
      .filter(
        (model) => (getLlmModelIntelligenceTier(model) ?? LlmModelIntelligenceTier.Basic) === tier
      )
      .sort(compareByPickerSortOrder);
    for (const model of tierModels) {
      options.push({ id: model.id, model, group: LLM_MODEL_PICKER_TIER_LABELS[tier] });
    }
  }
  return options;
}
